import React, { useState, useEffect, useRef, useMemo } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import toastError from "../../errors/toastError";
import PhoneMaskInput from "../PhoneMaskInput";
import LogoLoader from "../LogoLoader";
import ContactAvatar from "../ContactAvatar";
import useContactFieldTemplates from "../../hooks/useContactFieldTemplates";
import { toFormFields, fromFormFields } from "../../helpers/contactFields";

const useStyles = makeStyles(theme => ({
	paper: {
		borderRadius: 16,
	},
	header: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(2),
		padding: theme.spacing(3, 3, 2.5),
	},
	headerText: {
		flex: 1,
		minWidth: 0,
	},
	title: {
		fontSize: 20,
		fontWeight: 700,
		lineHeight: 1.3,
	},
	subtitle: {
		marginTop: 4,
		color: theme.palette.text.secondary,
	},
	closeButton: {
		marginTop: -6,
		marginRight: -8,
		color: theme.palette.text.secondary,
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(3),
		padding: theme.spacing(3),
		// barra de rolagem igual à do menu lateral
		...sidebarScrollbar(theme),
	},
	section: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		minHeight: 32,
	},
	sectionTitle: {
		fontSize: 12,
		fontWeight: 600,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
	},
	identity: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(2),
	},
	avatar: {
		width: 64,
		height: 64,
		fontSize: 24,
		fontWeight: 600,
		marginTop: theme.spacing(1),
		flexShrink: 0,
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	identityFields: {
		flex: 1,
		minWidth: 0,
		display: "flex",
		flexDirection: "column",
	},
	twoCols: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "minmax(0, 1fr)",
			gap: 0,
		},
	},
	origin: {
		color: theme.palette.text.secondary,
		"& strong": { color: theme.palette.text.primary, fontWeight: 600 },
	},
	required: {
		color: theme.palette.error.main,
	},
	addFieldButton: {
		textTransform: "none",
		fontWeight: 600,
	},
	emptyInfo: {
		margin: 0,
		padding: theme.spacing(1.75),
		borderRadius: 10,
		border: `1px dashed ${theme.palette.divider}`,
		textAlign: "center",
		color: theme.palette.text.secondary,
		fontSize: 13,
	},
	extraRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	extraName: {
		flex: "0 0 38%",
	},
	extraValue: {
		flex: 1,
	},
	actions: {
		padding: theme.spacing(2, 3),
		gap: theme.spacing(1),
	},
	actionButton: {
		textTransform: "none",
		fontWeight: 600,
		borderRadius: 10,
		height: 40,
		padding: theme.spacing(0, 2.5),
	},
	cancelButton: {
		color: theme.palette.text.primary,
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.trim()
		.min(2, "Nome muito curto")
		.max(50, "Nome muito longo (máx. 50 caracteres)")
		.required("Informe o nome do contato"),
	// número opcional: contato sem WhatsApp (lead manual) é permitido
	number: Yup.string().matches(/^55\d{10,11}$/, {
		message: "Número inválido. Ex: +55 (98) 99897-7943",
		excludeEmptyString: true,
	}),
	email: Yup.string().email("E-mail inválido"),
});

const tagChipStyle = tag => ({
	backgroundColor: tag.color || "#999",
	color: "#fff",
	fontWeight: 600,
});

const sameTagIds = (a = [], b = []) => {
	const ids = list => list.map(t => t.id).sort((x, y) => x - y).join(",");
	return ids(a) === ids(b);
};

const SectionHeader = ({ classes, title, action }) => (
	<div className={classes.sectionHeader}>
		<Typography component="h3" className={classes.sectionTitle}>
			{title}
		</Typography>
		{action}
	</div>
);

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		ownerId: "",
		customerSince: "",
		birthDate: "",
		tags: [],
	};

	const [contact, setContact] = useState(initialState);
	const [loadingData, setLoadingData] = useState(false);
	const [users, setUsers] = useState([]);
	const [tagOptions, setTagOptions] = useState([]);
	const templates = useContactFieldTemplates();

	// campos do modelo da ficha viram inputs fixos; o resto segue como
	// informação adicional livre
	const formInitialValues = useMemo(() => {
		const { templateValues, freeInfo } = toFormFields(contact.extraInfo, templates);
		return { ...contact, tags: contact.tags || [], extraInfo: freeInfo, templateValues };
	}, [contact, templates]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		const loadUsers = async () => {
			try {
				const { data } = await api.get("/users/");
				if (isMounted.current) setUsers(data.users || []);
			} catch (err) {
				// lista de responsáveis é opcional — não bloqueia o modal
			}
		};
		const loadTags = async () => {
			try {
				const { data } = await api.get("/tags/list");
				// tags de Kanban são etapas do atendimento, não do contato
				if (isMounted.current) {
					setTagOptions((data || []).filter(t => t.kanban !== 1));
				}
			} catch (err) {
				// sem tags carregadas o campo apenas fica sem opções
			}
		};
		loadUsers();
		loadTags();
	}, [open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			setLoadingData(true);
			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (isMounted.current) setLoadingData(false);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const syncTags = async (id, tags) => {
		try {
			await api.put(`/contacts/${id}/tags`, { tagIds: tags.map(t => t.id) });
		} catch (err) {
			// o contato já foi salvo; só as tags falharam
			toastError(err);
		}
	};

	const handleSaveContact = async values => {
		try {
			const { templateValues, tags, ...rest } = values;
			const payload = {
				...rest,
				extraInfo: fromFormFields(templateValues, values.extraInfo),
				ownerId: values.ownerId === "" ? null : values.ownerId,
				customerSince: values.customerSince || null,
				birthDate: values.birthDate || null,
			};
			const tagsChanged = !sameTagIds(tags, contact.tags || []);
			if (contactId) {
				await api.put(`/contacts/${contactId}`, payload);
				if (tagsChanged) await syncTags(contactId, tags);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", payload);
				if (tags.length > 0 && data?.id) await syncTags(data.id, tags);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			scroll="paper"
			classes={{ paper: classes.paper }}
			aria-labelledby="contact-modal-title"
		>
			{loadingData && <LogoLoader overlay />}
			<Formik
				initialValues={formInitialValues}
				enableReinitialize={true}
				validationSchema={ContactSchema}
				onSubmit={async values => {
					// Aguarda a requisição terminar: enquanto isso o Formik mantém
					// isSubmitting=true e o botão desabilitado, evitando envio duplicado.
					await handleSaveContact(values);
				}}
			>
				{({ values, errors, touched, isSubmitting, setFieldValue }) => (
					<Form style={{ display: "contents" }}>
						<div className={classes.header}>
							<div className={classes.headerText}>
								<Typography id="contact-modal-title" component="h2" className={classes.title}>
									{contactId ? "Editar contato" : "Novo contato"}
								</Typography>
								<Typography variant="body2" className={classes.subtitle}>
									{contactId
										? "Atualize os dados do contato."
										: "Só o nome é obrigatório. O resto pode ser preenchido depois."}
								</Typography>
							</div>
							<IconButton
								className={classes.closeButton}
								onClick={handleClose}
								aria-label="Fechar"
								disabled={isSubmitting}
							>
								<CloseIcon />
							</IconButton>
						</div>

						<DialogContent dividers className={classes.content}>
							{/* Contato */}
							<section className={classes.section}>
								<SectionHeader classes={classes} title="Contato" />
								<div className={classes.identity}>
									<ContactAvatar
										name={values.name || "?"}
										src={contact.profilePicUrl}
										className={classes.avatar}
										style={values.name ? undefined : { backgroundColor: theme.palette.action.selected, color: theme.palette.text.secondary }}
									/>
									<div className={classes.identityFields}>
										<Field
											as={TextField}
											label={
												<>
													Nome <span className={classes.required}>*</span>
												</>
											}
											name="name"
											placeholder="Ex.: Agrofox Petshop"
											autoFocus
											fullWidth
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											InputLabelProps={{ shrink: true }}
										/>
										<PhoneMaskInput
											value={values.number}
											onChange={val => setFieldValue("number", val)}
											label="WhatsApp"
											fullWidth
											error={touched.number && Boolean(errors.number)}
											helperText={
												(touched.number && errors.number) ||
												"Opcional. Sem número, o contato não recebe mensagens."
											}
										/>
									</div>
								</div>
								<Field
									as={TextField}
									label="E-mail"
									name="email"
									inputProps={{ inputMode: "email" }}
									placeholder="nome@empresa.com.br"
									error={touched.email && Boolean(errors.email)}
									helperText={touched.email && errors.email}
									fullWidth
									margin="dense"
									variant="outlined"
									InputLabelProps={{ shrink: true }}
								/>
								{contactId && contact?.whatsapp?.name && (
									<Typography variant="body2" className={classes.origin}>
										Conexão de origem: <strong>{contact.whatsapp.name}</strong>
									</Typography>
								)}
							</section>

							{/* Relacionamento */}
							<section className={classes.section}>
								<SectionHeader classes={classes} title="Relacionamento" />
								<Autocomplete
									multiple
									size="small"
									options={tagOptions}
									value={values.tags || []}
									onChange={(e, value) => setFieldValue("tags", value)}
									getOptionLabel={option => option.name || ""}
									getOptionSelected={(option, value) => option.id === value.id}
									filterSelectedOptions
									noOptionsText="Nenhuma etiqueta disponível"
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												key={option.id}
												size="small"
												label={option.name}
												style={tagChipStyle(option)}
												{...getTagProps({ index })}
											/>
										))
									}
									renderInput={params => (
										<TextField
											{...params}
											label="Etiquetas"
											variant="outlined"
											margin="dense"
											placeholder={(values.tags || []).length ? "" : "Selecione as tags"}
											InputLabelProps={{ shrink: true }}
										/>
									)}
								/>
								<TextField
									select
									label="Responsável (carteira)"
									value={values.ownerId || ""}
									onChange={e => setFieldValue("ownerId", e.target.value)}
									fullWidth
									margin="dense"
									variant="outlined"
									SelectProps={{ native: true }}
									InputLabelProps={{ shrink: true }}
								>
									<option value="">Sem responsável</option>
									{users.map(user => (
										<option key={user.id} value={user.id}>
											{user.name}
										</option>
									))}
								</TextField>
								<div className={classes.twoCols}>
									<TextField
										type="date"
										label="Cliente desde"
										value={values.customerSince || ""}
										onChange={e => setFieldValue("customerSince", e.target.value)}
										helperText="Se vazio, usamos a data de cadastro."
										margin="dense"
										variant="outlined"
										InputLabelProps={{ shrink: true }}
										fullWidth
									/>
									<TextField
										type="date"
										label="Aniversário"
										value={values.birthDate || ""}
										onChange={e => setFieldValue("birthDate", e.target.value)}
										margin="dense"
										variant="outlined"
										InputLabelProps={{ shrink: true }}
										fullWidth
									/>
								</div>
							</section>

							{/* Ficha do contato (campos do modelo da empresa) */}
							{values.templateValues && values.templateValues.length > 0 && (
								<section className={classes.section}>
									<SectionHeader classes={classes} title="Ficha do contato" />
									{values.templateValues.map((field, index) => (
										<TextField
											key={field.name}
											label={field.isAlert ? `⚠ ${field.name}` : field.name}
											value={field.value || ""}
											onChange={e =>
												setFieldValue(`templateValues[${index}].value`, e.target.value)
											}
											helperText={
												field.isAlert
													? "Campo de alerta: aparece em vermelho na ficha e no chat"
													: undefined
											}
											InputLabelProps={
												field.isAlert ? { style: { color: "#E53935" } } : undefined
											}
											fullWidth
											multiline
											margin="dense"
											variant="outlined"
										/>
									))}
								</section>
							)}

							{/* Informações adicionais (campos livres) */}
							<FieldArray name="extraInfo">
								{({ push, remove }) => (
									<section className={classes.section}>
										<SectionHeader
											classes={classes}
											title="Informações adicionais"
											action={
												<Button
													color="primary"
													size="small"
													startIcon={<AddIcon />}
													className={classes.addFieldButton}
													onClick={() => push({ name: "", value: "" })}
												>
													Adicionar campo
												</Button>
											}
										/>
										{(!values.extraInfo || values.extraInfo.length === 0) && (
											<p className={classes.emptyInfo}>
												Nenhuma informação extra. Use para CPF, endereço, observações…
											</p>
										)}
										{values.extraInfo &&
											values.extraInfo.map((info, index) => (
												<div className={classes.extraRow} key={`${index}-info`}>
													<Field
														as={TextField}
														label={i18n.t("contactModal.form.extraName")}
														name={`extraInfo[${index}].name`}
														variant="outlined"
														margin="dense"
														className={classes.extraName}
													/>
													<Field
														as={TextField}
														label={i18n.t("contactModal.form.extraValue")}
														name={`extraInfo[${index}].value`}
														variant="outlined"
														margin="dense"
														className={classes.extraValue}
													/>
													<IconButton
														size="small"
														onClick={() => remove(index)}
														aria-label="Remover campo"
													>
														<DeleteOutlineIcon fontSize="small" />
													</IconButton>
												</div>
											))}
									</section>
								)}
							</FieldArray>
						</DialogContent>

						<DialogActions className={classes.actions}>
							<Button
								onClick={handleClose}
								disabled={isSubmitting}
								className={`${classes.actionButton} ${classes.cancelButton}`}
							>
								{i18n.t("contactModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								disableElevation
								className={`${classes.actionButton} ${classes.btnWrapper}`}
							>
								{contactId ? "Salvar alterações" : "Salvar contato"}
								{isSubmitting && (
									<CircularProgress size={24} className={classes.buttonProgress} />
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default ContactModal;
